import "../LmidModal/LmidModal.scss"

import React, { FunctionComponent, useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"

import InputSearch from "components/InputSearch/InputSearch"
import SelectLoadingMessage from "components/Select/SelectLoadingMessage"
import SelectNoOptionsMessage from "components/Select/SelectNoOptionsMessage"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import useClickOutside from "hooks/useClickOutside"
import useExistingLmidSearch from "components/LmidModal/useExistingLmidSearch"
import ExistingLmidData, { ExistingLmidStatus } from "models/ExistingLmidData"
import LmidModalNotUsed, { INotUsedLmidData } from "components/LmidModal/LmidModalResult/LmidModalNotUsed"
import LmidModalCurrentTenant from "components/LmidModal/LmidModalResult/LmidModalCurrentTenant"
import LmidModalDifferentTenantOrDisposed from "components/LmidModal/LmidModalResult/LmidModalDifferentTenantOrDisposed"
import LmidAsset from "models/LmidAsset"
import { getZIndex } from "utils/modal"
import { IApiLink } from "@/types"
import { ICopyAssetLmidOverwrite } from "./copyAssetRows"

const DROPDOWN_MIN_WIDTH = 480
const DROPDOWN_VIEWPORT_MARGIN = 8
const DROPDOWN_PREFERRED_HEIGHT = 320

export interface ICopyAssetLmidSearchProps {
    /** Article number of the source asset — a tenant asset's LMID must match it. */
    articleNumber: string
    isInternalArticle: boolean
    /** Manually entered LMIDs of the other copies, to block duplicates within the dialog. */
    takenLmids: string[]
    /** 1-based copy number that already uses a duplicate LMID, by LMID. */
    takenLmidCopyNumbers: Record<string, number>
    value: string
    isInvalid?: boolean
    /** Focus the input and look the value up immediately, for activation by typing. */
    autoFocus?: boolean
    testId?: string
    onChange(text: string): void
    onReset(): void
    onAssign(lmid: string, createAssetLink: IApiLink | null, overwrite: ICopyAssetLmidOverwrite | null): void
}

/**
 * The LMID lookup of the copy dialog as an inline control: the same search
 * input and result branches as the add-asset flow, with the results in a
 * dropdown rendered through a portal so it can overflow the scrolling card
 * area. A tenant asset's LMID is only selectable when it belongs to the
 * source's article, an existing non-disposed asset of the own tenant cannot be
 * edited from here, and an LMID already entered for another copy is rejected.
 */
const CopyAssetLmidSearch: FunctionComponent<React.PropsWithChildren<ICopyAssetLmidSearchProps>> = ({
    articleNumber,
    isInternalArticle,
    takenLmids,
    takenLmidCopyNumbers,
    value,
    isInvalid,
    autoFocus,
    testId,
    onChange,
    onReset,
    onAssign,
}) => {
    const { t } = useTranslation()
    const { search, reset, isSearching, existingLmidData } = useExistingLmidSearch()
    const [shouldShowDropdownContent, setShouldShowDropdownContent] = useState(!!autoFocus)
    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null)
    const [dropdownEl, setDropdownEl] = useState<HTMLDivElement | null>(null)
    const zIndex = useMemo(() => getZIndex(), [])
    const hasDropdownContent = isSearching || !!existingLmidData || existingLmidData === null
    const isDropdownOpen = shouldShowDropdownContent && hasDropdownContent

    const exceptNodes = useMemo(() => [dropdownEl], [dropdownEl])
    useClickOutside(() => {
        setShouldShowDropdownContent(false)
    }, anchorEl, exceptNodes)

    useEffect(() => {
        if (autoFocus && value) search(value)
        // the seed lookup only runs for the initially typed text
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!isDropdownOpen) return
        function closeOnScroll(event: Event) {
            if (dropdownEl && event.target instanceof Node && dropdownEl.contains(event.target)) return
            setShouldShowDropdownContent(false)
        }
        window.addEventListener("scroll", closeOnScroll, true)
        window.addEventListener("resize", closeOnScroll)
        return () => {
            window.removeEventListener("scroll", closeOnScroll, true)
            window.removeEventListener("resize", closeOnScroll)
        }
    }, [isDropdownOpen, dropdownEl])

    function duplicateCopyNumber(lmid: string): number | undefined {
        return takenLmids.includes(lmid.trim()) ? takenLmidCopyNumbers[lmid.trim()] : undefined
    }

    function handleNotUsedLmidSelect(data: INotUsedLmidData, createAssetLink: IApiLink) {
        const hasCmmData = !!existingLmidData?.cmmAsset && isInternalArticle
        onAssign(data.lmid, createAssetLink, hasCmmData
            ? {
                drawingNo: data.drawingNumber ?? null,
                serialNumber: data.serialNumber ?? null,
                constructionYear: data.constructionYear ?? null,
            }
            : null)
        finishSelection()
    }

    function handleTenantAssetSelect(data: { existingLmidData: ExistingLmidData, assetData: LmidAsset }) {
        onAssign(data.existingLmidData.lmid, data.existingLmidData.createAssetLink ?? null, {
            drawingNo: data.existingLmidData.drawingNo,
            serialNumber: data.existingLmidData.serialNumber,
            constructionYear: data.existingLmidData.constructionYear,
            assetData: data.assetData,
        })
        finishSelection()
    }

    function finishSelection() {
        reset()
        setShouldShowDropdownContent(false)
    }

    function handleChange(text: string) {
        setShouldShowDropdownContent(true)
        onChange(text)
        search(text)
    }

    function handleReset() {
        reset()
        setShouldShowDropdownContent(false)
        onReset()
    }

    /**
     * Fixed placement next to the input: below it by default, flipped above it
     * when the space below is too tight for the result panels and the space
     * above is larger — so the lookup of the last card stays fully visible.
     */
    function dropdownStyle(): React.CSSProperties {
        const rect = anchorEl?.getBoundingClientRect()
        const width = Math.max(rect?.width ?? 0, DROPDOWN_MIN_WIDTH)
        const left = Math.max(
            DROPDOWN_VIEWPORT_MARGIN,
            Math.min(rect?.left ?? 0, window.innerWidth - width - DROPDOWN_VIEWPORT_MARGIN),
        )
        const spaceBelow = window.innerHeight - (rect?.bottom ?? 0) - 3 * DROPDOWN_VIEWPORT_MARGIN
        const spaceAbove = (rect?.top ?? 0) - 3 * DROPDOWN_VIEWPORT_MARGIN
        const openUpward = spaceBelow < DROPDOWN_PREFERRED_HEIGHT && spaceAbove > spaceBelow

        const common: React.CSSProperties = {
            position: "fixed",
            left,
            right: "auto",
            width,
            overflowY: "auto",
            zIndex,
        }
        if (openUpward) {
            return {
                ...common,
                bottom: window.innerHeight - (rect?.top ?? 0) + DROPDOWN_VIEWPORT_MARGIN,
                maxHeight: Math.max(spaceAbove, 0),
            }
        }
        return {
            ...common,
            top: (rect?.bottom ?? 0) + DROPDOWN_VIEWPORT_MARGIN,
            maxHeight: Math.max(spaceBelow, 0),
        }
    }

    const duplicateOfCopy = existingLmidData ? duplicateCopyNumber(existingLmidData.lmid) : undefined
    const portalTarget = document.getElementById("root-select-menu-portal") ?? document.body

    return (
        <div className="lmid-modal__search" ref={setAnchorEl}>
            <InputSearch
                placeholder={t('lmidModal.searchFieldPlaceholder')}
                value={value}
                isInvalid={isInvalid}
                autoFocus={autoFocus}
                data-testid={testId}
                onChange={handleChange}
                onFocus={() => setShouldShowDropdownContent(true)}
                onReset={handleReset}
            />
            {isDropdownOpen && createPortal(
                <div className="lmid-modal__search-dropdown" style={dropdownStyle()} ref={setDropdownEl}>
                    {isSearching &&
                        <SelectLoadingMessage />
                    }
                    {!isSearching &&
                        <>
                            {existingLmidData === null &&
                                <SelectNoOptionsMessage>
                                    <div className="max-w-70 mx-auto">
                                        {t('lmidModal.lmidNotFound')}
                                    </div>
                                </SelectNoOptionsMessage>
                            }
                            {!!existingLmidData && duplicateOfCopy !== undefined &&
                                <div className="py-4 px-3 text-center">
                                    <ValidationMessage>
                                        {t('copyAsset.lmidDuplicateInDialog', { copyNumber: duplicateOfCopy })}
                                    </ValidationMessage>
                                </div>
                            }
                            {!!existingLmidData && duplicateOfCopy === undefined &&
                                <>
                                    {existingLmidData.status === ExistingLmidStatus.notUsed &&
                                        <LmidModalNotUsed
                                            existingLmidData={existingLmidData}
                                            isInternalArticle={isInternalArticle}
                                            onNotUsedLmidSelect={handleNotUsedLmidSelect}
                                            onSelectArticleClick={reset}
                                            onClose={finishSelection}
                                        />
                                    }
                                    {existingLmidData.status === ExistingLmidStatus.active &&
                                        <LmidModalCurrentTenant
                                            existingLmidData={existingLmidData}
                                            message={t('copyAsset.lmidAlreadyExists')}
                                        />
                                    }
                                    {(existingLmidData.status === ExistingLmidStatus.usedInDifferentTenant
                                        || existingLmidData.status === ExistingLmidStatus.disposed) &&
                                        <LmidModalDifferentTenantOrDisposed
                                            existingLmidData={existingLmidData}
                                            shouldBeEqualToArticleNumber={articleNumber}
                                            articleMismatchMessage={existingLmidData.status === ExistingLmidStatus.disposed
                                                ? t('copyAsset.lmidDifferentArticle')
                                                : undefined}
                                            onAssetFromDifferentTenantOrDisposedSelect={handleTenantAssetSelect}
                                        />
                                    }
                                </>
                            }
                        </>
                    }
                </div>,
                portalTarget,
            )}
        </div>
    )
}

export default CopyAssetLmidSearch
